import React, { memo, useEffect } from 'react';

import Slice from '../slices/slice';
import Text from '../slices/text';
import Image from '../slices/image';
import ImageText from '../slices/image-text';
import TextText from '../slices/text-text';
import Hero from '../slices/hero';
import Product from '../slices/product';
import ProductList from '../slices/product-list';
import Video from '../slices/video';
import VideoText from '../slices/video-text';
import CallToAction from '../slices/call-to-action';

import colorMap from '../slices/shared/color-map';

import { trackContentViewed } from '../../tracking';
import { trackChangelogArticleViewed } from '../../../changelog/tracking';

import type {
  PrismicArticleData,
  PrismicSlice,
  PrismicArticleType,
  PrismicSlicePrimary,
} from '../../../../types';

import { PageContent, ArticleComponent } from './styles';
import PopularProducts from '../slices/popular-products';
import FAQ from '../slices/faq';
import Categories from '../slices/categories';
import ArticleCarousel from '../slices/article-carousel';
import Columns from '../slices/columns';
import Newsletter from '../slices/newsletter';

type OwnProps = {
  id: string;
  data: PrismicArticleData;
  type: PrismicArticleType;
};

type Props = OwnProps;

const getBackgroundColor = (primary: PrismicSlicePrimary) => {
  if ('heroBackgroundColor' in primary && primary.heroBackgroundColor) {
    return primary.heroBackgroundColor;
  }

  if ('backgroundColor' in primary) {
    return primary.backgroundColor;
  }

  if ('textColor' in primary) {
    return primary.textColor;
  }

  return null;
};

export const Article = ({ id, data, type }: Props) => {
  useEffect(() => {
    trackContentViewed(id);

    if (type === 'CHANGELOG') {
      trackChangelogArticleViewed({ id });
    }
  }, []);

  const renderSlice = (slice: PrismicSlice, index: number) => {
    const key = `${slice.type}_${index}`;
    const backgroundColor = getBackgroundColor(slice.primary);
    const colors = colorMap(backgroundColor);

    switch (slice.primary.__typename) {
      case 'PrismicSliceHeroTitle':
        return (
          <Hero
            key={key}
            {...slice.primary}
            textColor={colors.text}
            heroBackgroundColor={colors.bg}
          />
        );
      case 'PrismicSliceText':
        return (
          <Slice key={key} colors={colors}>
            <PageContent alignLeft>
              <Text data={slice.primary.text} />
            </PageContent>
          </Slice>
        );
      case 'PrismicSliceImage':
        return (
          <Slice key={key} colors={colors}>
            <PageContent alignLeft>
              <Image {...slice.primary} />
            </PageContent>
          </Slice>
        );
      case 'PrismicSliceImageText':
        return (
          <Slice key={key} colors={colors}>
            <PageContent alignLeft>
              <ImageText {...slice.primary} />
            </PageContent>
          </Slice>
        );
      case 'PrismicSliceTextImage':
        return (
          <Slice key={key} colors={colors}>
            <PageContent alignLeft>
              <ImageText {...slice.primary} reverse />
            </PageContent>
          </Slice>
        );
      case 'PrismicSliceTextText':
        return (
          <Slice key={key} colors={colors}>
            <PageContent alignLeft>
              <TextText {...slice.primary} />
            </PageContent>
          </Slice>
        );
      case 'PrismicSliceProduct':
        return slice.primary.product ? (
          <Slice key={key} colors={colors}>
            <PageContent alignLeft>
              <Product {...slice.primary} />
            </PageContent>
          </Slice>
        ) : null;
      case 'PrismicSlicePopularProducts':
        return slice.primary.popularFacet ? (
          <Slice key={key} colors={colors}>
            <PageContent alignLeft>
              <PopularProducts {...slice.primary} />
            </PageContent>
          </Slice>
        ) : null;
      case 'PrismicSliceProductList':
        return slice.primary.products ? (
          <Slice key={key} colors={colors}>
            <PageContent alignLeft>
              <ProductList {...slice.primary} />
            </PageContent>
          </Slice>
        ) : null;
      case 'PrismicSliceVideo':
        return (
          <Slice key={key} colors={colors}>
            <PageContent alignLeft>
              <Video data={slice.primary.video} />
            </PageContent>
          </Slice>
        );
      case 'PrismicSliceVideoText':
        return (
          <Slice key={key} colors={colors}>
            <PageContent alignLeft>
              <VideoText data={slice.primary} />
            </PageContent>
          </Slice>
        );
      case 'PrismicSliceTextVideo':
        return (
          <Slice key={key} colors={colors}>
            <PageContent alignLeft>
              <VideoText reverse data={slice.primary} />
            </PageContent>
          </Slice>
        );
      case 'PrismicSliceCallToAction':
        return (
          <Slice key={key} colors={colors}>
            <PageContent alignLeft>
              <CallToAction {...slice.primary} />
            </PageContent>
          </Slice>
        );
      case 'PrismicSliceFaqSection':
        return (
          <Slice key={key} colors={colors}>
            <PageContent alignLeft>
              <FAQ {...slice.primary} />
            </PageContent>
          </Slice>
        );
      case 'PrismicSliceCategories':
        return (
          <Slice key={key} colors={colors}>
            <PageContent alignLeft>
              <Categories {...slice.primary} />
            </PageContent>
          </Slice>
        );
      case 'PrismicSliceArticleCarousel':
        return (
          <Slice key={key} colors={colors}>
            <PageContent alignLeft>
              <ArticleCarousel {...slice.primary} />
            </PageContent>
          </Slice>
        );
      case 'PrismicSliceColumns':
        return (
          <Slice key={key} colors={colors}>
            <PageContent alignLeft>
              <Columns {...slice.primary} />
            </PageContent>
          </Slice>
        );
      case 'PrismicSliceNewsletter':
        return (
          <Slice key={key} colors={colors}>
            <PageContent alignLeft>
              <Newsletter {...slice.primary} />
            </PageContent>
          </Slice>
        );
      default:
        return null;
    }
  };

  return <ArticleComponent>{data.body.map(renderSlice)}</ArticleComponent>;
};

export default memo(Article);
